import { formatElapsed, formatTime, useAuthStore } from '@sentinel/web-core';
import { Link } from '@tanstack/react-router';
import { Icon } from '@sentinel/ui';
import { useTranslation } from 'react-i18next';
import type { MissionSummary } from '@sentinel/shared';

interface Props {
  current: MissionSummary | null;
  missions: readonly MissionSummary[];
  now: number;
  onChoose: (id: string) => void;
  onLeave: () => void;
}

function CurrentDetails({ mission, now }: { mission: MissionSummary; now: number }) {
  const { t } = useTranslation();
  return (
    <>
      <span className="dt">
        <Icon name="map" size={14} />
        {mission.sectorsCount > 0
          ? t('shell.mission.placeSectors', { place: mission.place, count: mission.sectorsCount })
          : mission.place}
      </span>
      <span className="dt">
        <Icon name="person" size={14} />
        <span>
          {t('shell.mission.openedBy', {
            time: formatTime(mission.openedAt),
            by: mission.openedBy ?? '—',
          })}{' '}
          · <span className="mono">{formatElapsed(mission.openedAt, now)}</span>{' '}
          {t('shell.mission.sinceOpening')}
        </span>
      </span>
    </>
  );
}

/** The open operations of the organization: the active one with where and since when it runs
 *  (what the dashboard header used to say), the others one click away. */
export function MissionMenu({ current, missions, now, onChoose, onLeave }: Props) {
  const { t } = useTranslation();
  const organization = useAuthStore((s) => s.me?.organization?.name ?? '');
  return (
    <div className="msel-menu" role="listbox" aria-label={t('shell.mission.selectLabel')}>
      <span className="mlabel">{t('shell.mission.openIn', { organization })}</span>
      {missions.map((m) => {
        const active = m.id === current?.id;
        return (
          <button
            key={m.id}
            type="button"
            role="option"
            aria-selected={active}
            className="msel-opt"
            onClick={() => onChoose(m.id)}
          >
            {active ? <Icon name="check" size={16} /> : <span className="ph" />}
            <span>
              <span className="nm">{m.name}</span>
              <span className="mt">
                {m.code} · {m.protocol}
              </span>
              {active ? (
                <CurrentDetails mission={m} now={now} />
              ) : (
                <span className="dt">
                  {m.place} ·{' '}
                  {t('shell.mission.teamsOf', {
                    teams: m.counts.teams,
                    total: m.counts.teamsTotal,
                  })}
                </span>
              )}
            </span>
          </button>
        );
      })}
      <div className="msel-foot">
        <Link to="/operations" className="btn sm" onClick={onLeave}>
          {t('shell.mission.all')}
        </Link>
        <p className="note" style={{ marginTop: 7 }}>
          {t('shell.mission.oneAtATime')}
        </p>
      </div>
    </div>
  );
}
